import React from 'react';
import { Box, InputBase, MenuItem, Select, Typography } from '@mui/material';
import { DIAL_CODES, nationalLengthForDial } from '../constants/phoneDialCodes';

const ACCENT = '#3B82F6';

interface PhoneDialFieldProps {
  id: string;
  dialCode: string;
  national: string;
  onDialCodeChange: (code: string) => void;
  onNationalChange: (digits: string) => void;
  onBlur: () => void;
  error: boolean;
  helperText?: string;
  label?: string;
  disabled?: boolean;
  compact?: boolean;
}

function PhoneDialField({
  id,
  dialCode,
  national,
  onDialCodeChange,
  onNationalChange,
  onBlur,
  error,
  helperText,
  label,
  disabled,
  compact,
}: PhoneDialFieldProps): React.ReactElement {
  const maxLen = nationalLengthForDial(dialCode);

  return (
    <Box>
      <Typography
        component="label"
        htmlFor={id}
        sx={{
          display: 'block',
          fontSize: compact ? 12 : 14,
          fontWeight: 500,
          mb: compact ? 0.75 : 1,
          color: 'text.primary',
          textAlign: 'left',
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          borderRadius: '12px',
          overflow: 'hidden',
          bgcolor: 'rgba(248, 250, 252, 1)',
          border: compact ? '1px solid rgba(219, 224, 230, 1)' : 'none',
          boxShadow: error ? '0 0 0 2px #d32f2f' : 'none',
          opacity: disabled ? 0.6 : 1,
          '&:focus-within': {
            boxShadow: `0 0 0 2px ${compact ? '#111827' : ACCENT}`,
            borderColor: compact ? '#111827' : undefined,
          },
        }}
      >
        <Select
          value={dialCode}
          disabled={disabled}
          onChange={(event) => {
            const next = String(event.target.value);
            onDialCodeChange(next);
            const nextMax = nationalLengthForDial(next);
            if (national.length > nextMax) onNationalChange(national.slice(0, nextMax));
          }}
          variant="standard"
          disableUnderline
          aria-label="Código de país"
          sx={{
            minWidth: compact ? 104 : 112,
            px: compact ? 1.25 : 1.5,
            borderRight: '1px solid',
            borderColor: 'rgba(226, 232, 240, 1)',
            fontSize: 14,
            '& .MuiSelect-select': { py: compact ? 1.15 : 1.75, pr: 3 },
          }}
        >
          {DIAL_CODES.map((opt) => (
            <MenuItem key={opt.code} value={opt.code}>
              {opt.country} +{opt.code}
            </MenuItem>
          ))}
        </Select>
        <InputBase
          id={id}
          name="phone"
          fullWidth
          disabled={disabled}
          placeholder={dialCode === '52' ? '33 1234 5678' : 'Número'}
          value={national}
          onChange={(event) => {
            const digits = event.target.value.replace(/\D/g, '').slice(0, maxLen);
            onNationalChange(digits);
          }}
          onBlur={onBlur}
          inputProps={{
            maxLength: maxLen,
            inputMode: 'numeric',
            pattern: '[0-9]*',
          }}
          onKeyDown={(event) => {
            const allowed = [
              'Backspace',
              'Tab',
              'Delete',
              'ArrowLeft',
              'ArrowRight',
              'Home',
              'End',
            ];
            if (allowed.includes(event.key)) return;
            if (!/^\d$/.test(event.key)) event.preventDefault();
          }}
          sx={{
            px: compact ? 1.5 : 2,
            py: compact ? 0.85 : 1.75,
            '& input': { fontSize: compact ? 14 : 16 },
          }}
        />
      </Box>
      {helperText ? (
        <Typography
          variant="caption"
          color={error ? 'error' : 'text.secondary'}
          sx={{ mt: 0.5, display: 'block', textAlign: 'left' }}
        >
          {helperText}
        </Typography>
      ) : null}
    </Box>
  );
}

PhoneDialField.defaultProps = {
  helperText: '',
  label: 'Teléfono (WhatsApp)',
  disabled: false,
  compact: false,
};

export default PhoneDialField;
